import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointemploymentDetails";

import { EMPLOYMENT } from "app/constants/apiEndPoints";
import { JointemploymentDetailsFromOne } from "app/utils/types";
import { useEmployment } from "app/hooks/employment/useEmployment";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import DynamicFormOne from "app/components/Individual/employmentDetails/dynamicForms/DynamicFormOne";

const PartOne: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync: submitEmployment, isPending } = useEmployment();
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const { formOne, ...others } = useSelector(
    (state: RootState) => state.jointemploymentDetails.stepsData
  );
  const applicant = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    register,
    clearErrors,
    watch,
  } = useForm<JointemploymentDetailsFromOne>({
    defaultValues: {
      yourDetails: {
        currentEmployment: "",
        currentOccupation: "",
        employerName: "",
        abn: "",
        employerAddress: {
          street: "",
          state: "",
          postCode: "",
        },
        employerPhoneNumber: "",
      },
      coBorrowerDetails: {
        currentEmployment: "",
        currentOccupation: "",
        employerName: "",
        abn: "",
        employerAddress: {
          street: "",
          state: "",
          postCode: "",
        },
        employerPhoneNumber: "",
      },
    },
  });
  const yourEmploymentType = watch("yourDetails.currentEmployment");
  const coEmploymentType = watch("coBorrowerDetails.currentEmployment");

  const onSubmit = async (data: JointemploymentDetailsFromOne) => {
    let { yourDetails, coBorrowerDetails } = data || {};

    if (yourDetails?.currentEmployment === "unemployed_or_retired") {
      yourDetails = {
        ...yourDetails,
        employerPhoneNumber: "",
        employerAddress: {
          street: "",
          state: "",
          postCode: "",
        },
        employerName: "",
        abn: "",
        currentOccupation: "",
      };
    }
    if (coBorrowerDetails?.currentEmployment === "unemployed_or_retired") {
      coBorrowerDetails = {
        ...coBorrowerDetails,
        employerPhoneNumber: "",
        employerAddress: {
          street: "",
          state: "",
          postCode: "",
        },
        employerName: "",
        abn: "",
        currentOccupation: "",
      };
    }

    const request1 = {
      url: `${EMPLOYMENT}/${savedIds?.employmentID}/first`,
      data: yourDetails,
    };
    const request2 = {
      url: `${EMPLOYMENT}/${savedIds?.secondEmploymentID}/first`,
      data: coBorrowerDetails,
    };
    const storedData = {
      yourDetails: yourDetails,
      coBorrowerDetails: coBorrowerDetails,
    };

    try {
      const [response1, response2] = await Promise.all([
        submitEmployment(request1),
        submitEmployment(request2),
      ]);
      if (response1?.success && response2?.success) {
        dispatch(nextStep());
        dispatch(
          addStepsData({
            ...others,
            formOne: storedData,
          })
        );
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  useEffect(() => {
    if (formOne) {
      reset(formOne);
    } else {
      const { YourDetails, Coborrower } = applicant || {};

      if (YourDetails?.employment?.employmentType) {
        setValue(
          "yourDetails.currentEmployment",
          YourDetails.employment.employmentType
        );
      }
      if (YourDetails?.employment?.employerName) {
        setValue(
          "yourDetails.employerName",
          YourDetails.employment.employerName
        );
      }

      if (Coborrower?.employment?.employmentType) {
        setValue(
          "coBorrowerDetails.currentEmployment",
          Coborrower.employment.employmentType
        );
      }
      if (Coborrower?.employment?.employerName) {
        setValue(
          "coBorrowerDetails.employerName",
          Coborrower.employment.employerName
        );
      }
    }
  }, [formOne, reset]);

  const handlePrevious = () => {
    navigate(`/joint-personal-details`);
  };

  useEffect(() => {
    if (
      yourEmploymentType === "unemployed_or_retired" ||
      yourEmploymentType === "contractor" ||
      yourEmploymentType === "self-employed"
    ) {
      clearErrors("yourDetails.currentOccupation");
      clearErrors("yourDetails.employerName");
      clearErrors("yourDetails.employerPhoneNumber");
      clearErrors("yourDetails.employerAddress.postCode");
      clearErrors("yourDetails.employerAddress.state");
      clearErrors("yourDetails.employerAddress.street");
    }
  }, [yourEmploymentType]);
  useEffect(() => {
    if (
      coEmploymentType === "unemployed_or_retired" ||
      coEmploymentType === "contractor" ||
      coEmploymentType === "self-employed"
    ) {
      clearErrors("coBorrowerDetails.currentOccupation");
      clearErrors("coBorrowerDetails.employerName");
      clearErrors("coBorrowerDetails.employerPhoneNumber");
      clearErrors("coBorrowerDetails.employerAddress.postCode");
      clearErrors("coBorrowerDetails.employerAddress.state");
      clearErrors("coBorrowerDetails.employerAddress.street");
    }
  }, [coEmploymentType]);

  return (
    <>
      <ProgressBar progress={0} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Employment Details" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row gap-3 sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              <DynamicFormOne
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                currentEmployment="yourDetails.currentEmployment"
                currentOccupation="yourDetails.currentOccupation"
                employerName="yourDetails.employerName"
                employerPhoneNumber="yourDetails.employerPhoneNumber"
                addressStreetName="yourDetails.employerAddress.street"
                addressStateName="yourDetails.employerAddress.state"
                addressPostCodeName="yourDetails.employerAddress.postCode"
                abn="yourDetails.abn"
                currentEmploymentType={yourEmploymentType}
              />
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />
              <DynamicFormOne
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                currentEmployment="coBorrowerDetails.currentEmployment"
                currentOccupation="coBorrowerDetails.currentOccupation"
                employerName="coBorrowerDetails.employerName"
                employerPhoneNumber="coBorrowerDetails.employerPhoneNumber"
                addressStreetName="coBorrowerDetails.employerAddress.street"
                addressStateName="coBorrowerDetails.employerAddress.state"
                addressPostCodeName="coBorrowerDetails.employerAddress.postCode"
                abn="coBorrowerDetails.abn"
                currentEmploymentType={coEmploymentType}
              />
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom onClick={handlePrevious}>Previous</PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartOne;
